import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(categoryId || 're-men-tui-jian');

  useEffect(() => {
    if (categoryId) {
      setActiveCategory(categoryId);
    }
  }, [categoryId]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const filteredArticles = articles.filter((a) => a.categoryId === activeCategory);
  const otherArticles = articles.filter((a) => a.categoryId !== activeCategory).slice(0, 3);

  const handleCategoryChange = (slug: string) => {
    setActiveCategory(slug);
    navigate(`/category/${slug}`);
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || '分类'} - 美女视频推荐`,
    description: currentCategory?.description || '',
    url: `https://pozino.cn/category/${activeCategory}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          {/* Page Header */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
              <div className="mt-4">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {currentCategory?.name}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
                  {currentCategory?.description}
                </p>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="w-full px-4 md:px-6 py-8 border-b border-background-200/70 sticky top-16 bg-background-50/95 backdrop-blur-md z-40">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Articles Grid */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-7xl mx-auto">
              {filteredArticles.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                    {filteredArticles.map((article) => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>

          {/* Other Categories Preview */}
          {otherArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    更多精彩内容
                  </h2>
                  <p className="text-foreground-500 text-sm">探索其他分类的热门文章</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {otherArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}